import { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Buildings, Plus, Trash, PencilSimple, EnvelopeSimple, Phone } from "@phosphor-icons/react";
import { toast } from "sonner";

const empty = { name: "", nif_cif: "", email: "", phone: "", address: "", notes: "" };

export default function Providers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/providers"); setItems(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditingId(null); setForm(empty); setOpen(true); };
  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name || "", nif_cif: p.nif_cif || "",
      email: p.email || "", phone: p.phone || "", address: p.address || "", notes: p.notes || "",
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Nombre obligatorio"); return; }
    if (!form.address.trim()) { toast.error("Dirección obligatoria"); return; }
    setBusy(true);
    try {
      if (editingId) await api.put(`/providers/${editingId}`, form);
      else await api.post("/providers", form);
      toast.success(editingId ? "Proveedor actualizado" : "Proveedor creado");
      setOpen(false); setForm(empty); setEditingId(null);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (p) => {
    if (!window.confirm(`¿Eliminar el proveedor "${p.name}"?`)) return;
    try { await api.delete(`/providers/${p.id}`); toast.success("Proveedor eliminado"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--ag-text-dim)]">Directorio de la organización</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mt-1">Proveedores</h1>
          <p className="text-sm text-[var(--ag-text-dim)] mt-2">Suministradores con los que trabajas: químicos, alquiler, productos, asesorías, etc. Vincúlalos a tus gastos para análisis por proveedor.</p>
        </div>
        <Button onClick={startCreate} className="ag-btn-gold rounded-sm" data-testid="prov-new-btn">
          <Plus className="mr-1.5" /> Nuevo proveedor
        </Button>
      </header>

      <div className="ag-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[var(--ag-midnight-soft)] hover:bg-[var(--ag-midnight-soft)]">
              <TableHead className="text-[10px] uppercase tracking-[0.15em]">Nombre</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.15em]">NIF/CIF</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.15em]">Email</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.15em]">Teléfono</TableHead>
              <TableHead className="text-right text-[10px] uppercase tracking-[0.15em]">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={5} className="text-center text-[var(--ag-text-dim)] py-10">Cargando…</TableCell></TableRow>}
            {!loading && items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-[var(--ag-text-dim)] py-10">Sin proveedores. Pulsa <b>“Nuevo proveedor”</b> para añadir el primero.</TableCell></TableRow>}
            {items.map((p) => (
              <TableRow key={p.id} data-testid={`prov-row-${p.id}`}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2"><Buildings size={14} weight="duotone" className="text-[var(--ag-gold)]" /> {p.name}</div>
                  {p.notes && <div className="text-[11px] text-[var(--ag-text-dim)] mt-0.5">{p.notes}</div>}
                </TableCell>
                <TableCell className="font-mono-tab text-xs">{p.nif_cif || "—"}</TableCell>
                <TableCell className="text-xs">
                  {p.email ? <a href={`mailto:${p.email}`} className="inline-flex items-center gap-1 hover:text-[var(--ag-gold)]"><EnvelopeSimple size={11} /> {p.email}</a> : "—"}
                </TableCell>
                <TableCell className="text-xs">
                  {p.phone ? <a href={`tel:${p.phone}`} className="inline-flex items-center gap-1 hover:text-[var(--ag-gold)]"><Phone size={11} /> {p.phone}</a> : "—"}
                </TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" onClick={()=>startEdit(p)} data-testid={`prov-edit-${p.id}`} title="Editar"><PencilSimple size={14} /></Button>
                  <Button size="icon" variant="ghost" onClick={()=>del(p)} data-testid={`prov-del-${p.id}`} title="Eliminar"><Trash size={14} className="text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={(o)=>{ setOpen(o); if (!o) { setEditingId(null); setForm(empty); } }}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl tracking-tighter">{editingId ? "Editar proveedor" : "Nuevo proveedor"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Nombre *</Label>
              <Input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="mt-1 rounded-sm" data-testid="prov-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs uppercase tracking-wider">CIF / NIF</Label>
                <Input value={form.nif_cif} onChange={(e)=>setForm({...form, nif_cif: e.target.value.toUpperCase()})} className="mt-1 rounded-sm font-mono-tab" data-testid="prov-form-nif" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Teléfono</Label>
                <Input value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} className="mt-1 rounded-sm" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Email</Label>
              <Input type="email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Dirección *</Label>
              <Input value={form.address} onChange={(e)=>setForm({...form, address: e.target.value})}
                className="mt-1 rounded-sm" placeholder="Calle, número, ciudad, CP" required data-testid="prov-form-address" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Notas internas</Label>
              <Input value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} className="mt-1 rounded-sm" placeholder="Condiciones, contacto, etc." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={submit} disabled={busy || !form.name.trim()} className="bg-[var(--ag-gold)] hover:bg-[#FFCD3C] rounded-sm" data-testid="prov-form-submit">
              {busy ? "Guardando…" : (editingId ? "Actualizar" : "Crear")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
